/**
 * SF-5087: pick the companies that want an item or suggestion. The options come from sf-api's
 * company search (active Paid and Demo companies only; it checks every id again on save), read
 * 300 ms after the last keystroke as the other admin searches do.
 *
 * A form can start with more than the cap (Eva's Create item takes every company of its
 * suggestions). Each change is passed on as made, so a removal removes only that company; no
 * company can be added while the cap is reached, and a message says how many to remove.
 */
import { useEffect, useRef, useState } from 'react';
import { Select, Space, Tag, Typography } from 'antd';
import { usePriorityCompanies, type PriorityCompany } from '../api/prioritiesClient';
import { COMPANIES_MAX } from '../constants';

const SEARCH_DEBOUNCE_MS = 300;

interface Props {
  id?: string;
  value?: string[];
  onChange?: (ids: string[]) => void;
  /** The companies already picked, so their names show before any search. */
  initial?: PriorityCompany[];
}

/** The companies picked for a suggestion or item, as tags. */
export const CompanyTags: React.FC<{ companies?: PriorityCompany[] }> = ({ companies = [] }) =>
  companies.length > 0 ? (
    <Space size={[4, 4]} wrap>
      {companies.map((c) => (
        <Tag key={c.id}>{c.name}</Tag>
      ))}
    </Space>
  ) : null;

export const CompanySelect: React.FC<Props> = ({ id, value = [], onChange, initial = [] }) => {
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  // Read only once the person opens the box, not every time a form holding it renders.
  const [opened, setOpened] = useState(false);
  useEffect(() => {
    const handle = setTimeout(() => setQ(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search]);
  const query = usePriorityCompanies(q, opened);
  // Every name seen, so a picked company keeps its name when a later search leaves it out.
  const names = useRef(new Map<string, string>());
  for (const c of [...initial, ...(query.data?.data?.companies ?? [])]) {
    names.current.set(c.id, c.name);
  }
  const found = query.data?.data?.companies ?? [];
  const options = [
    ...value
      .filter((v) => !found.some((c) => c.id === v))
      .map((v) => ({
        value: v,
        label: names.current.get(v) ?? 'Company',
      })),
    ...found.map((c) => ({
      value: c.id,
      label: c.name,
      disabled: value.length >= COMPANIES_MAX && !value.includes(c.id),
    })),
  ];
  const over = value.length - COMPANIES_MAX;
  const change = (ids: string[]) => {
    // At or above the cap nothing new goes in; removals always do.
    const blocked = ids.length > COMPANIES_MAX && ids.some((v) => !value.includes(v));
    onChange?.(blocked ? ids.filter((v) => value.includes(v)) : ids);
  };
  return (
    <>
      <Select
        id={id}
        mode="multiple"
        showSearch
        allowClear
        filterOption={false}
        searchValue={search}
        onSearch={setSearch}
        onOpenChange={(visible) => visible && setOpened(true)}
        onChange={change}
        value={value}
        options={options}
        loading={query.isFetching}
        placeholder="Type to search companies"
        notFoundContent={
          query.isError
            ? 'Companies could not be loaded.'
            : // Nothing to say about matches until a read has answered (the first one, or a new
              // search after one that found none).
              !query.isSuccess || (query.isFetching && found.length === 0)
              ? 'Loading companies…'
              : 'No companies found'
        }
      />
      {over > 0 && (
        <Typography.Text type="danger" role="alert" style={{ display: 'block', marginTop: 4 }}>
          An item can have at most {COMPANIES_MAX} companies. Remove {over} more to save.
        </Typography.Text>
      )}
    </>
  );
};
